import type { AddressAttributes, SettlementTaxDetailsInput } from '../../../generated/api.graphql';
import { type AddressInput, toGqlInput as toAddressInput } from '../Address/AddressInput';
import type { TaxWithholdingType } from './TaxWithholdingType';

/**
 * The tax section as `TaxDetails.vue` holds it. `taxWithholdingAmount` is `number | string` because
 * an emptied `VPPriceInput` hands back `''`, and telling that apart from a real `0` is the whole of
 * step 6 below.
 */
export type TaxDetailsInput = {
  businessName?: string | null;
  taxId?: string | null;
  taxWithholdingAmount?: number | string | null;
  taxWithholdingType?: TaxWithholdingType | null;
  taxLabel?: string | null;
  address?: AddressInput | null;
};

/**
 * Empty means cleared. `0` and `false` are real values and must survive, and a string that is only
 * whitespace is empty -- `Number('   ')` is `0`, which on the withholding field would write a real
 * figure where the user meant to clear one.
 */
const clearable = <T>(value: T | '' | null | undefined): T | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return typeof value === 'string' && value.trim() === '' ? null : value;
};

/**
 * Present enough to send, for the two keys the model rejects as null. Deliberately expressed in
 * terms of `clearable` rather than repeating its checks: Rails' `presence` validation uses
 * `blank?`, so a whitespace-only `taxLabel` is rejected exactly like a null one -- sending it would
 * fail the whole section instead of leaving the stored value alone. Sharing the one emptiness rule
 * is what keeps the two from drifting apart again.
 */
const present = (value: unknown): boolean => clearable(value) !== null;

/**
 * `null` clears the withholding, `undefined` leaves it alone. An empty input means the user cleared
 * the field; anything non-empty that will not parse is a bug upstream, and must not be allowed to
 * read as a clear -- this is the figure the payout is computed from.
 */
const amount = (value?: number | string | null): number | null | undefined => {
  const cleared = clearable(value);
  if (cleared === null) {
    return null;
  }

  const parsed = Number(cleared);

  return Number.isNaN(parsed) ? undefined : parsed;
};

// Clearing is this form's own rule -- `App/Address` whitelists but never normalizes, because the
// venue form sends its values through untouched. A null id would build a new `Address` on every
// save just as a missing one does, so it is omitted rather than cleared.
const addressInput = (address: AddressInput): AddressAttributes => toAddressInput({
  id: address.id ?? undefined,
  street1: clearable(address.street1),
  street2: clearable(address.street2),
  city: clearable(address.city),
  state: clearable(address.state),
  postalCode: clearable(address.postalCode),
  country: clearable(address.country),
});

/**
 * Build the tax section AppSchema writes.
 *
 * The resolver does `attrs = tax_details.to_h; settlement.update(attrs)`, so a key that is sent as
 * null clears its column and a key that is omitted is left alone. That is what makes clearing
 * reachable at last -- the legacy `updateEventArtistSettlement` was a presence-gated patch
 * (`attrs[:x] = args[:x] if args[:x]`), so nothing could ever be blanked.
 *
 * Which side of the line each key falls on is decided by the model, not by taste:
 *
 * - `businessName`, `taxId`, `taxWithholdingAmount` -- nullable columns with no validation, so they
 *   are **always sent**, empty normalized to null. These are the fields the AC is about. The one
 *   exception is an unparseable amount, which is omitted rather than cleared; see `amount`.
 * - `taxLabel`, `taxWithholdingType` -- `validates :tax_label, presence: true` and an `inclusion`
 *   validation with no `allow_nil`. A null for either fails the *whole* section, taking business
 *   name, tax id and the address down with it, so they are **omitted when empty**. That reproduces
 *   the legacy presence gate for exactly these two. Do not "simplify" them into the group above.
 * - `settlementType` -- a member of this input, and never written here. `saveSettlementType` is its
 *   only writer; see the note on that function.
 */
export const toGqlInput = (details: TaxDetailsInput): SettlementTaxDetailsInput => {
  const input: SettlementTaxDetailsInput = {
    businessName: clearable(details.businessName),
    taxId: clearable(details.taxId),
  };

  const withholdingAmount = amount(details.taxWithholdingAmount);

  if (withholdingAmount !== undefined) {
    input.taxWithholdingAmount = withholdingAmount;
  }

  if (present(details.taxLabel)) {
    input.taxLabel = details.taxLabel;
  }

  if (present(details.taxWithholdingType)) {
    input.taxWithholdingType = details.taxWithholdingType as SettlementTaxDetailsInput['taxWithholdingType'];
  }

  if (details.address) {
    input.address = addressInput(details.address);
  }

  return input;
};
